import { ChevronLeft, ChevronRight, Download, X, ZoomIn, ZoomOut } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn.ts";
import { IDENTITY, MAX_SCALE, clamp, zoomAt, type View } from "./zoomPan.ts";

export interface ViewerImage {
  src: string;
  name: string;
  /** where "download" points (defaults to src) */
  downloadUrl?: string;
}

type Open = (images: ViewerImage[], index?: number) => void;
const Ctx = createContext<Open>(() => {});

/** Opens images over the page instead of a new tab (like Claude Code on the Mac). */
export function useImageViewer(): Open {
  return useContext(Ctx);
}

export function ImageViewerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ images: ViewerImage[]; index: number } | null>(null);
  const open = useCallback<Open>((images, index = 0) => { if (images.length) setState({ images, index: Math.max(0, Math.min(index, images.length - 1)) }); }, []);
  return (
    <Ctx.Provider value={open}>
      {children}
      {state && createPortal(<Viewer images={state.images} index={state.index} onIndex={(i) => setState({ ...state, index: i })} onClose={() => setState(null)} />, document.body)}
    </Ctx.Provider>
  );
}

function Viewer({ images, index, onIndex, onClose }: { images: ViewerImage[]; index: number; onIndex: (i: number) => void; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const rtl = i18n.dir() === "rtl";
  const img = images[index]!;
  const many = images.length > 1;
  const prev = () => many && onIndex((index - 1 + images.length) % images.length);
  const next = () => many && onIndex((index + 1) % images.length);

  // zoom and pan by transform (pinch, wheel, double tap, drag): the old "zoom" laid the picture out at full size in
  // a centred, scrolling box, which cut off its top and left with no way to reach them
  const boxRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [view, setViewState] = useState<View>(IDENTITY);
  const viewRef = useRef<View>(IDENTITY);
  const [dragging, setDragging] = useState(false);
  const setView = (v: View) => { viewRef.current = v; setViewState(v); };
  const geometry = () => {
    const b = boxRef.current!.getBoundingClientRect();
    return { box: { w: b.width, h: b.height, cx: b.left + b.width / 2, cy: b.top + b.height / 2 }, fitted: { w: imgRef.current?.offsetWidth ?? 0, h: imgRef.current?.offsetHeight ?? 0 } };
  };
  const apply = (v: View) => { const g = geometry(); setView(clamp(v, g.fitted, g.box)); };
  const zoomTo = (s: number, client?: { x: number; y: number }) => {
    const g = geometry();
    const p = client ? { x: client.x - g.box.cx, y: client.y - g.box.cy } : { x: 0, y: 0 };
    apply(zoomAt(viewRef.current, s, p));
  };
  useEffect(() => setView(IDENTITY), [index]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "+" || e.key === "=") zoomTo(viewRef.current.s * 1.5);
      else if (e.key === "-") zoomTo(viewRef.current.s / 1.5);
      else if (e.key === "0") setView(IDENTITY);
      // physical arrows: in right-to-left reading the right arrow goes back
      else if (viewRef.current.s === 1 && e.key === "ArrowRight") (rtl ? prev : next)();
      else if (viewRef.current.s === 1 && e.key === "ArrowLeft") (rtl ? next : prev)();
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = overflow; };
  });

  // the wheel (and a trackpad pinch, which arrives as a wheel with ctrl) zooms around the cursor
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomTo(viewRef.current.s * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.002)), { x: e.clientX, y: e.clientY });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  type Pt = { x: number; y: number };
  const pointers = useRef(new Map<number, Pt>());
  const gesture = useRef<{ kind: "pan" | "pinch" | "swipe"; start: Pt; startView: View; startDist: number; startMid: Pt; moved: boolean; onImage: boolean } | null>(null);
  const lastTap = useRef<{ at: number; p: Pt } | null>(null);
  const mid = (a: Pt, b: Pt) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);
  const begin = (onImage: boolean) => {
    const pts = [...pointers.current.values()];
    if (pts.length >= 2) gesture.current = { kind: "pinch", start: pts[0]!, startView: viewRef.current, startDist: dist(pts[0]!, pts[1]!) || 1, startMid: mid(pts[0]!, pts[1]!), moved: true, onImage };
    else if (pts.length === 1) gesture.current = { kind: viewRef.current.s > 1 ? "pan" : "swipe", start: pts[0]!, startView: viewRef.current, startDist: 1, startMid: pts[0]!, moved: false, onImage };
  };
  const onPointerDown = (e: React.PointerEvent) => {
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* a pointer the browser no longer tracks */ }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    begin(e.target === imgRef.current);
    setDragging(true);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    const pts = [...pointers.current.values()];
    if (g.kind === "pinch" && pts.length >= 2) {
      const m = mid(pts[0]!, pts[1]!);
      const geo = geometry();
      const z = zoomAt(g.startView, g.startView.s * (dist(pts[0]!, pts[1]!) / g.startDist), { x: g.startMid.x - geo.box.cx, y: g.startMid.y - geo.box.cy });
      apply({ s: z.s, x: z.x + m.x - g.startMid.x, y: z.y + m.y - g.startMid.y });
      return;
    }
    const p = pts[0]!;
    const dx = p.x - g.start.x;
    const dy = p.y - g.start.y;
    if (Math.hypot(dx, dy) > 8) g.moved = true;
    if (g.kind === "pan") apply({ ...g.startView, x: g.startView.x + dx, y: g.startView.y + dy });
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const g = gesture.current;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size > 0) { begin(g?.onImage ?? false); return; } // one finger of a pinch lifted: keep panning
    setDragging(false);
    gesture.current = null;
    if (!g) return;
    const p = { x: e.clientX, y: e.clientY };
    if (g.kind === "swipe" && g.moved) {
      const dx = p.x - g.start.x;
      const dy = p.y - g.start.y;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) ((dx < 0) !== rtl ? next : prev)();
      else if (dy > 90) onClose();
      return;
    }
    if (g.moved) return;
    // a tap: a mouse click on the picture zooms in or out; on a touch screen that takes a double tap
    const now = Date.now();
    const double = !!lastTap.current && now - lastTap.current.at < 300 && dist(lastTap.current.p, p) < 30;
    lastTap.current = double ? null : { at: now, p };
    if ((e.pointerType === "mouse" && g.onImage) || double) {
      if (viewRef.current.s > 1) setView(IDENTITY);
      else zoomTo(2.5, p);
    } else if (!g.onImage && e.pointerType === "mouse") onClose(); // a click beside the picture closes it
  };

  const btn = "tap grid place-items-center rounded-lg p-2 text-white/80 hover:bg-white/10 hover:text-white";
  return (
    <div role="dialog" aria-modal="true" aria-label={img.name} className="fixed inset-0 z-[120] flex flex-col bg-black/90 backdrop-blur-sm pt-safe pb-safe">
      <div className="flex items-center gap-1 px-3 py-2 text-white">
        <span dir="ltr" className="min-w-0 flex-1 truncate text-sm text-white/80">{img.name}{many ? ` · ${index + 1}/${images.length}` : ""}</span>
        <button type="button" onClick={() => zoomTo(viewRef.current.s / 1.5)} disabled={view.s <= 1} className={cn(btn, "disabled:opacity-30")} aria-label={t("viewer.zoomOut")} title={t("viewer.zoomOut")}><ZoomOut className="size-5" /></button>
        <button type="button" onClick={() => setView(IDENTITY)} disabled={view.s === 1} className={cn(btn, "min-w-12 font-mono text-xs disabled:opacity-60")} aria-label={t("viewer.fit")} title={t("viewer.fit")}>{Math.round(view.s * 100)}%</button>
        <button type="button" onClick={() => zoomTo(viewRef.current.s * 1.5)} disabled={view.s >= MAX_SCALE} className={cn(btn, "disabled:opacity-30")} aria-label={t("viewer.zoomIn")} title={t("viewer.zoomIn")}><ZoomIn className="size-5" /></button>
        <a href={img.downloadUrl ?? img.src} download={img.name} className={btn} aria-label={t("viewer.download")} title={t("viewer.download")}><Download className="size-5" /></a>
        <button type="button" onClick={onClose} className={btn} aria-label={t("common.close")}><X className="size-5" /></button>
      </div>
      <div
        ref={boxRef}
        className={cn("relative flex min-h-0 flex-1 touch-none select-none items-center justify-center overflow-hidden p-3", view.s > 1 ? (dragging ? "cursor-grabbing" : "cursor-grab") : "cursor-zoom-in")}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          ref={imgRef}
          src={img.src}
          alt={img.name}
          draggable={false}
          className={cn("max-h-full max-w-full rounded-md object-contain shadow-2xl will-change-transform", !dragging && "transition-transform duration-200 ease-out", /\.svg$/i.test(img.name) && "w-[min(100%,48rem)] bg-white")}
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})` }}
        />
        {many && view.s === 1 && (
          <>
            <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={prev} className="tap absolute start-3 top-1/2 grid -translate-y-1/2 place-items-center rounded-full bg-black/50 p-2 text-white hover:bg-black/70" aria-label={t("viewer.previous")}><ChevronLeft className="size-6 rtl:-scale-x-100" /></button>
            <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={next} className="tap absolute end-3 top-1/2 grid -translate-y-1/2 place-items-center rounded-full bg-black/50 p-2 text-white hover:bg-black/70" aria-label={t("viewer.next")}><ChevronRight className="size-6 rtl:-scale-x-100" /></button>
          </>
        )}
      </div>
    </div>
  );
}
